import { Injectable } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OESettingsService } from "./oe-settings.service";
import { OENNotificationType } from "../../data/enums/oe-notification-type.enum";
import { OEUtilityService } from "./oe-utility.service";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { BFXNotificationType } from "../../../../shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../../shared/cubiq-services/model/core/notifications/BFXNotificationSubType";
import { BFXNotificationEventType } from "../../../../shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { NotificationService } from "../../../../shared/cubiq-services/lookup/notification/impl/NotificationService";

@Injectable()
export class OENotificationsService {
  private notificationEventSource = new Subject<any>();
  public notifyCtrl$ = this.notificationEventSource.asObservable();

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private settings: OESettingsService,
    private utility: OEUtilityService,
    private translate: TranslateService,
    private notifService: NotificationService,
  ) {}

  /**********************************************************************/
  /* METHODS  */

  public sendErrorNotification(
    opr: OENNotificationType,
    response: any,
    orderId: string,
    orderRef: string,
  ): void {
    let defined: boolean = false;
    let type: BFXNotificationType = BFXNotificationType.ORDER_ACTION_FAIL;
    let subType: BFXNotificationSubType = BFXNotificationSubType.MODIFY_OUT_FAIL;
    let message: string = null;

    switch (opr) {
      case OENNotificationType.ERROR_MODIFY_OUT_PERMISSIONS: // 'modify_out_permissions'
        if (
          response != null &&
          response.lockErrors != null &&
          response.lockErrors.errorMessages.length > 0
        ) {
          let knownErrors = [
            "order.notModifiable.triggered",
            "access.error.user.strategy.action.not.allowed",
            "order.lockedByAnotherUser",
          ];
          let error = JSON.parse(response.lockErrors.errorMessages[0]);
          defined = knownErrors.includes(error["id"]);
          message = this.translate.instant(
            error["id"],
            this.utility.sanityCheckArgs(error["args"]),
          );

          if (error.args[0].toString() !== orderId && error.args[0].toString() !== orderRef) {
            logger.out.error(
              `DEV-TEST OrderId in error message mismatch: orderId [${orderId}] != error orderId [${error.args[0].toString()}]`,
            );
          }
        }
        break;
      case OENNotificationType.ERROR_MODIFY_OUT_EDIT: //'modify_out_on_edit':
        if (
          response != null &&
          response.orderData == null &&
          response.lockErrors != null &&
          response.lockErrors.errorMessages.length > 0
        ) {
          let knownErrors = ["strategy.not.found.for.order"];
          let error = JSON.parse(response.lockErrors.errorMessages[0]);
          defined = knownErrors.includes(error["id"]);
          message = this.translate.instant(
            error["id"],
            this.utility.sanityCheckArgs(error["args"]),
          );

          if (
            error.args != null &&
            error.args.length > 0 &&
            error.args[0].toString() !== orderId &&
            error.args[0].toString() !== orderRef
          ) {
            logger.out.error(
              `DEV-TEST OrderId in error message mismatch: orderId [${orderId}] != error orderId [${error.args[0].toString()}]`,
            );
          }
        }

        break;
      case OENNotificationType.ERROR_MODIFY_OUT_SERVER_FAIL: //'modify_out_fail':
        if (
          response != null &&
          response.lockErrors != null &&
          response.lockErrors.errorMessages.length > 0
        ) {
          let error = JSON.parse(response.lockErrors.errorMessages[0]);
          message = this.translate.instant(
            error["id"],
            this.utility.sanityCheckArgs(error["args"]),
          );
          defined = true;

          if (
            error != null &&
            error.args[0] != null &&
            error.args[0].toString() !== orderId &&
            error.args[0].toString() !== orderRef
          ) {
            logger.out.error(
              `DEV-TEST OrderId in error message mismatch: orderId [${orderId}] != error orderId [${error.args[0].toString()}]`,
            );
          }
        } else if (response != null && response.orderData == null) {
          defined = true;
          message = this.translate.instant("order_entry.error_notification.order_data_missing");
        }
        break;
      case OENNotificationType.ERROR_MODIFY_INFO_ACTIONED:
        defined = true;
        message = this.translate.instant("order_entry.error_notification.modify_info_actioned");
        break;
      case OENNotificationType.ERROR_MODIFY_INFO_EXPIRED:
        defined = true;
        message = this.translate.instant("order_entry.error_notification.modify_info_expired");
        break;
    }
    if (defined) {
      this.notifyOnErrors(response, type, subType, orderId, orderRef, message);
    } else {
      logger.out.error(
        `ERROR: _oeWorker.sendErrorNotification(): Failed to define and send notification!`,
      );
    }
  }

  /*  */
  notifyOnErrors(
    response: any,
    notificationType: BFXNotificationType,
    notificationSubType: BFXNotificationSubType,
    orderId: string,
    orderRef: string,
    message: string,
  ) {
    if (response != null) {
      if (response.lockErrors != null && response.lockErrors.errorMessages.length > 0) {
        response.lockErrors.errorMessages.forEach((errorItem: any) => {
          this.notifService.fireNotificationEvent(
            this.notifService.orderEntryDataToNotification(
              response,
              notificationType,
              notificationSubType,
              orderId,
              orderRef,
              message,
            ),
            BFXNotificationEventType.ORDER_ENTRY_EVENT,
          );
          // this.notificationEventSource.next({result: response, type: notificationType, subType: notificationSubType, ref: orderId, message: message});
        });
      }
    } else {
      // this.notificationEventSource.next({result: new Object(), type: notificationType, subType: notificationSubType, ref: orderId, message: message});
      this.notifService.fireNotificationEvent(
        this.notifService.orderEntryDataToNotification(
          new Object(),
          notificationType,
          notificationSubType,
          orderId,
          orderRef,
          message,
        ),
        BFXNotificationEventType.ORDER_ENTRY_EVENT,
      );
    }
  }

  notifyServerOperationErrors(
    errorMessages: string[],
    notificationType: BFXNotificationType,
    notificationSubType: BFXNotificationSubType,
    orderId: string,
    orderRef: string,
  ) {
    // Notification doesn't do newline separation at the moment, but can write a pipe later if wanted
    const errorMessage = errorMessages.join("\n");
    this.notifService.fireNotificationEvent(
      this.notifService.orderEntryDataToNotification(
        {},
        notificationType,
        notificationSubType,
        orderId,
        orderRef,
        errorMessage,
      ),
      BFXNotificationEventType.ORDER_ENTRY_EVENT,
    );
  }
}
